import React from 'react';
import { Shield, ChevronRight, Lock, Eye, Database, Share2, UserCheck, Clock, Globe, Mail, AlertCircle, FileText, Cpu, Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

const sections = [
  { id: 'commitment', label: 'Our Commitment' },
  { id: 'who-we-are', label: 'Who We Are' },
  { id: 'data-we-collect', label: 'Data We Collect' },
  { id: 'lawful-basis', label: 'Lawful Basis' },
  { id: 'how-we-use', label: 'How We Use Data' },
  { id: 'ai-processing', label: 'AI Processing' },
  { id: 'data-sharing', label: 'Data Sharing' },
  { id: 'international-transfers', label: 'International Transfers' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'candidate-rights', label: 'Candidate Rights' },
  { id: 'cookies', label: 'Cookies & Tracking' },
  { id: 'children', label: "Children's Privacy" },
  { id: 'security', label: 'Security Measures' },
  { id: 'policy-updates', label: 'Policy Updates' },
  { id: 'contact', label: 'Contact Us & DPO' },
];

function SectionCard({ id, icon: Icon, iconColor, title, children }: {
  id: string;
  icon: React.ElementType;
  iconColor: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div id={id} className="glass p-8 rounded-2xl scroll-mt-28 group hover:border-teal-500/40 transition-all duration-300 border border-white/10">
      <div className="flex items-start gap-4 mb-5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-2xl font-bold text-white leading-tight">{title}</h2>
      </div>
      <div className="text-slate-300 leading-relaxed space-y-4 ml-14">{children}</div>
    </div>
  );
}

function InfoBadge({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 bg-teal-500/10 border border-teal-500/20 rounded-xl p-4">
      <AlertCircle className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
      <p className="text-teal-200 text-sm">{children}</p>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <ChevronRight className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function DataTable({ rows }: { rows: { category: string; examples: string; purpose: string }[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-teal-500/10 border-b border-white/10">
            <th className="text-left px-4 py-3 text-teal-300 font-semibold">Category</th>
            <th className="text-left px-4 py-3 text-teal-300 font-semibold">Examples</th>
            <th className="text-left px-4 py-3 text-teal-300 font-semibold">Purpose</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/5' : ''}`}>
              <td className="px-4 py-3 font-medium text-white">{row.category}</td>
              <td className="px-4 py-3 text-slate-300">{row.examples}</td>
              <td className="px-4 py-3 text-slate-400">{row.purpose}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy | GreenTick - AI Ethics & Data Protection",
    description: "GreenTick's comprehensive privacy policy covers AI processing, data rights, security measures, and GDPR compliance. Learn how we protect your interactive interview data.",
    canonicalPath: "/privacy_policy"
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">

            {/* Back link */}
            <Link to="/" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8 group">
              <span className="group-hover:-translate-x-1 transition-transform">←</span>
              <span>Back to Home</span>
            </Link>

            {/* Hero Header */}
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center border border-teal-500/30">
                <Shield className="w-8 h-8 text-teal-400" />
              </div>
              <div>
                <h1 className="text-5xl md:text-6xl font-bold text-white">Privacy Policy</h1>
                <p className="text-slate-400 text-sm mt-1">GreenTick AI Recruitment Platform</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-12">
              <span className="bg-teal-500/20 text-teal-300 text-xs font-medium px-3 py-1.5 rounded-full border border-teal-500/30">
                Last Updated: March 29, 2026
              </span>
              <span className="bg-emerald-500/20 text-emerald-300 text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-500/30">
                Effective: March 29, 2026
              </span>
              <span className="bg-slate-600/30 text-slate-300 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-500/30">
                Version 2.0
              </span>
            </div>

            {/* Layout: TOC + Content */}
            <div className="flex flex-col lg:flex-row gap-10">

              {/* Table of Contents Sidebar */}
              <aside className="lg:w-64 flex-shrink-0">
                <div className="sticky top-28 glass p-6 rounded-2xl border border-white/10">
                  <h3 className="text-white font-bold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-teal-400" />
                    Contents
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((s) => (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="block text-sm text-slate-400 hover:text-teal-400 transition-colors py-1.5 px-2 rounded-lg hover:bg-teal-500/10 leading-tight"
                      >
                        {s.label}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 space-y-8">

                {/* Intro Para */}
                <div className="glass p-8 rounded-2xl border border-teal-500/20 bg-teal-500/5">
                  <p className="text-slate-200 leading-relaxed text-lg">
                    This Privacy Policy explains how <strong className="text-white">GreenTick</strong> (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) collects, uses, stores, shares, and protects information about you when you use our AI-powered recruitment platform at{' '}
                    <a href="https://greentick.org.in" className="text-teal-400 hover:underline">greentick.org.in</a>{' '}
                    and related services (collectively, the &ldquo;Platform&rdquo;).
                  </p>
                  <p className="text-slate-300 leading-relaxed mt-4">
                    Please read this policy carefully. By using GreenTick, you agree to the collection and use of information in accordance with this policy. If you do not agree, please discontinue use of the Platform.
                  </p>
                </div>

                {/* 1. Our Commitment */}
                <SectionCard id="commitment" icon={Shield} iconColor="bg-teal-500/20 text-teal-400" title="Our Commitment to Privacy">
                  <p>
                    At GreenTick, privacy is not an afterthought — it is foundational to how we build our product. We believe that candidates deserve to know exactly what data is used to evaluate them, and that recruiters deserve a platform they can trust with sensitive hiring decisions.
                  </p>
                  <BulletList items={[
                    'We collect only data that is strictly necessary for our services.',
                    'We never sell your personal information to third parties.',
                    'We implement enterprise-grade encryption and security controls.',
                    'We comply with applicable data protection laws including the Digital Personal Data Protection Act (DPDPA) 2023 (India), GDPR principles, and Information Technology Act, 2000.',
                    'We are transparent about algorithmic decision-making in our AI interviews.',
                    'We honor all data subject rights promptly and free of charge.',
                  ]} />
                </SectionCard>

                {/* 2. Who We Are */}
                <SectionCard id="who-we-are" icon={FileText} iconColor="bg-emerald-500/20 text-emerald-400" title="Who We Are (Data Controller)">
                  <p>
                    GreenTick is an AI recruitment technology company incorporated in India. For the purposes of applicable data protection legislation, GreenTick is a <strong className="text-white">Data Controller</strong> with respect to personal data you provide directly to us, and a <strong className="text-white">Data Processor</strong> when processing candidate data on behalf of our business customers (employers, universities).
                  </p>
                  <div className="bg-slate-800/50 rounded-xl p-5 border border-white/10 text-sm">
                    <p className="font-semibold text-white mb-2">Registered Entity</p>
                    <p>GreenTick Technologies Pvt. Ltd. (or applicable legal entity)</p>
                    <p className="text-slate-400 mt-1">Website: <a href="https://greentick.org.in" className="text-teal-400 hover:underline">https://greentick.org.in</a></p>
                    <p className="text-slate-400">Data Protection Contact: <a href="mailto:privacy@greentick.org.in" className="text-teal-400 hover:underline">privacy@greentick.org.in</a></p>
                  </div>
                </SectionCard>

                {/* 3. Data We Collect */}
                <SectionCard id="data-we-collect" icon={Database} iconColor="bg-purple-500/20 text-purple-400" title="Data We Collect">
                  <p>We collect different categories of data depending on how you interact with the Platform:</p>
                  <h3 className="text-white font-semibold text-lg mt-2">A. Information You Provide Directly</h3>
                  <DataTable rows={[
                    { category: 'Identity Data', examples: 'Name, date of birth, gender', purpose: 'Account creation and verification' },
                    { category: 'Contact Data', examples: 'Email address, phone number', purpose: 'Communication and notifications' },
                    { category: 'Professional Data', examples: 'Resume, work history, skills, education, CGPA', purpose: 'Job matching and candidate evaluation' },
                    { category: 'Interview Data', examples: 'Voice recordings, text responses, on-screen tool interactions', purpose: 'AI-powered skill and behaviour assessment' },
                    { category: 'Account Data', examples: 'Username, password (hashed), preferences', purpose: 'Platform access and personalization' },
                    { category: 'Payment Data', examples: 'Billing name, transaction ID (no full card data stored)', purpose: 'Payment processing via third-party gateway' },
                    { category: 'Feedback & Communications', examples: 'Support tickets, survey responses, emails', purpose: 'Support and service improvement' },
                  ]} />

                  <h3 className="text-white font-semibold text-lg mt-4">B. Information We Collect Automatically</h3>
                  <BulletList items={[
                    'IP address, browser type, device identifiers, and operating system',
                    'Pages visited, time spent, click paths, and referral sources',
                    'Error logs and performance data',
                    'Geolocation data (country/city-level, inferred from IP)',
                    'Cookie and tracking data (see Cookies section below)',
                  ]} />

                  <h3 className="text-white font-semibold text-lg mt-4">C. Information from Third Parties</h3>
                  <BulletList items={[
                    'Company/institute administrators who add you to the platform as a candidate',
                    'LinkedIn or other integrations if you choose to connect them',
                    'Analytics and advertising partners',
                  ]} />

                  <InfoBadge>
                    We do not collect sensitive personal data (e.g., caste, religion, political opinions, biometric data) unless explicitly required and consented to for a specific assessment.
                  </InfoBadge>
                </SectionCard>

                {/* 4. Lawful Basis */}
                <SectionCard id="lawful-basis" icon={FileText} iconColor="bg-blue-500/20 text-blue-400" title="Lawful Basis for Processing">
                  <p>We process your personal data on one or more of the following lawful bases:</p>
                  <div className="space-y-4">
                    {[
                      { basis: 'Consent', desc: 'When you register, take an AI interview, or opt-in to marketing communications. You may withdraw consent at any time.' },
                      { basis: 'Contract Performance', desc: 'When processing is necessary to provide the service you requested (e.g., conducting and evaluating your interview).' },
                      { basis: 'Legitimate Interests', desc: 'For analytics, fraud prevention, security improvements, and platform optimization — balanced against your rights.' },
                      { basis: 'Legal Obligation', desc: 'When we are required to retain or disclose data under applicable law.' },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
                        <div>
                          <p className="text-white font-semibold">{item.basis}</p>
                          <p className="text-slate-300 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>

                {/* 5. How We Use Data */}
                <SectionCard id="how-we-use" icon={Eye} iconColor="bg-teal-500/20 text-teal-400" title="How We Use Your Information">
                  <p>We use the information we collect for the following purposes:</p>
                  <BulletList items={[
                    'Conducting AI-powered screening interviews and generating evaluation reports',
                    'Matching candidates to job roles based on skill profiles',
                    'Providing personalized interview preparation and feedback',
                    'Enabling employers and institutions to review candidate performance',
                    'Processing payments for subscriptions and services',
                    'Sending transactional communications (interview invites, reports, account updates)',
                    'Sending marketing communications if you have opted in (unsubscribe anytime)',
                    'Analysing aggregate usage patterns to improve the Platform',
                    'Detecting and preventing fraud, abuse, and security incidents',
                    'Complying with legal and regulatory obligations',
                    'Training and improving our AI models (using anonymised/aggregated data only)',
                  ]} />
                  <InfoBadge>
                    We do NOT use your personal data to train AI models without either obtaining your explicit consent or first anonymising the data such that it cannot be linked back to you.
                  </InfoBadge>
                </SectionCard>

                {/* 6. AI Processing */}
                <SectionCard id="ai-processing" icon={Cpu} iconColor="bg-cyan-500/20 text-cyan-400" title="AI Processing & Automated Decision-Making">
                  <p>
                    GreenTick's core service involves AI-powered analysis of candidate responses. It is important you understand what this means:
                  </p>
                  <h3 className="text-white font-semibold text-lg">What Our AI Does</h3>
                  <BulletList items={[
                    'Asks adaptive interview questions based on your job role and responses',
                    'Analyses verbal responses for relevance, clarity, depth, and domain knowledge',
                    'Evaluates code written in on-screen coding environments',
                    'Assesses communication style, structure, and professional vocabulary',
                    'Generates a structured evaluation report with scores and reasoning',
                  ]} />
                  <h3 className="text-white font-semibold text-lg mt-4">What Our AI Does NOT Do</h3>
                  <BulletList items={[
                    'Make final hiring decisions — those are always made by human recruiters',
                    'Evaluate appearance, facial expressions, or physical characteristics',
                    'Discriminate based on accent, nationality, gender, or other protected characteristics',
                    'Use biometric data for identity verification (we use email-based auth)',
                  ]} />
                  <h3 className="text-white font-semibold text-lg mt-4">Your Right to Human Review</h3>
                  <p>
                    Consistent with GDPR Article 22 principles (applicable to our EU candidates) and best practices under DPDPA 2023, you have the right to request human review of any automated evaluation. Contact us at{' '}
                    <a href="mailto:privacy@greentick.org.in" className="text-teal-400 hover:underline">privacy@greentick.org.in</a>.
                  </p>
                </SectionCard>

                {/* 7. Data Sharing */}
                <SectionCard id="data-sharing" icon={Share2} iconColor="bg-rose-500/20 text-rose-400" title="Data Sharing & Disclosure">
                  <p className="font-semibold text-white">We never sell your personal data.</p>
                  <p>We may share your data in the following limited circumstances:</p>

                  <div className="space-y-4 mt-2">
                    {[
                      {
                        who: 'Employers / Educational Institutions',
                        what: 'Your interview evaluation reports, skill scores, and profile — only for roles you have applied to or been invited into.',
                      },
                      {
                        who: 'Technology & Cloud Service Providers',
                        what: 'Hosting (cloud infrastructure), AI model APIs, database services — bound by data processing agreements.',
                      },
                      {
                        who: 'Payment Processors',
                        what: 'Transaction data strictly necessary to process payments. We do not store full card details.',
                      },
                      {
                        who: 'Analytics Providers',
                        what: 'Aggregated, anonymised usage data to improve our product. No personal identifiers shared.',
                      },
                      {
                        who: 'Legal Authorities',
                        what: 'When required by law, court order, or to protect the safety of our users or the public.',
                      },
                      {
                        who: 'Business Transfers',
                        what: 'In the event of a merger, acquisition, or asset sale, your data may transfer with appropriate notice and protections.',
                      },
                    ].map((item, i) => (
                      <div key={i} className="bg-slate-800/40 rounded-xl p-4 border border-white/10">
                        <p className="text-white font-semibold text-sm mb-1">{item.who}</p>
                        <p className="text-slate-300 text-sm">{item.what}</p>
                      </div>
                    ))}
                  </div>
                </SectionCard>

                {/* 8. International Transfers */}
                <SectionCard id="international-transfers" icon={Globe} iconColor="bg-indigo-500/20 text-indigo-400" title="International Data Transfers">
                  <p>
                    GreenTick is headquartered in India. Your data is primarily stored and processed within India. Where we use third-party services that operate in other countries (e.g., AI infrastructure, cloud providers), we ensure appropriate safeguards are in place:
                  </p>
                  <BulletList items={[
                    'Data Processing Agreements (DPAs) with all third-party processors',
                    'Standard Contractual Clauses (SCCs) for transfers to countries without adequate protection (for EU data)',
                    'Binding Corporate Rules or equivalent mechanisms where applicable',
                    'Your data is not transferred to countries without adequate protection unless these safeguards are in place',
                  ]} />
                </SectionCard>

                {/* 9. Data Retention */}
                <SectionCard id="data-retention" icon={Clock} iconColor="bg-amber-500/20 text-amber-400" title="Data Retention">
                  <p>We retain your data only as long as necessary for the purposes described in this policy:</p>
                  <DataTable rows={[
                    { category: 'Account & Profile Data', examples: 'Identity, contact, professional info', purpose: 'Until account deletion + 30 days cool-off' },
                    { category: 'Interview Recordings & Reports', examples: 'Voice, text, evaluation scores', purpose: '12 months from interview date, unless employer requests extension' },
                    { category: 'Payment Records', examples: 'Transaction history', purpose: '7 years (legal/tax obligation)' },
                    { category: 'Log & Security Data', examples: 'IP logs, access logs', purpose: '90 days rolling' },
                    { category: 'Marketing Preferences', examples: 'Opt-in/opt-out records', purpose: 'Duration of relationship + 3 years after last interaction' },
                    { category: 'Support Communications', examples: 'Tickets, emails', purpose: '2 years from resolution' },
                  ]} />
                  <p className="mt-2">
                    After retention periods expire, we securely delete or anonymise your data. You may request early deletion subject to our legal obligations — see <a href="#your-rights" className="text-teal-400 hover:underline">Your Rights</a> below.
                  </p>
                </SectionCard>

                {/* 10. Your Rights */}
                <SectionCard id="your-rights" icon={UserCheck} iconColor="bg-emerald-500/20 text-emerald-400" title="Your Rights (All Users)">
                  <p>Depending on your location and applicable law, you have the following rights regarding your personal data:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { right: '📄 Right of Access', desc: 'Request a copy of all personal data we hold about you.' },
                      { right: '✏️ Right to Rectification', desc: 'Request correction of inaccurate or incomplete data.' },
                      { right: '🗑️ Right to Erasure', desc: 'Request deletion of your data ("right to be forgotten"), subject to legal retention obligations.' },
                      { right: '⏸️ Right to Restriction', desc: 'Request that we limit processing of your data in certain circumstances.' },
                      { right: '📦 Right to Portability', desc: 'Receive your data in a structured, machine-readable format to transfer to another service.' },
                      { right: '🚫 Right to Object', desc: 'Object to processing based on legitimate interests or for direct marketing.' },
                      { right: '🤖 Right re: Automated Decisions', desc: 'Request human review of decisions made solely by automated processing.' },
                      { right: '↩️ Right to Withdraw Consent', desc: 'Withdraw consent at any time without affecting prior processing.' },
                    ].map((item, i) => (
                      <div key={i} className="bg-slate-800/40 rounded-xl p-4 border border-white/10">
                        <p className="font-semibold text-white text-sm mb-1">{item.right}</p>
                        <p className="text-slate-400 text-sm">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4">
                    To exercise any of these rights, please email us at{' '}
                    <a href="mailto:privacy@greentick.org.in" className="text-teal-400 hover:underline">privacy@greentick.org.in</a>{' '}
                    with the subject line &ldquo;Data Rights Request — [Your Name]&rdquo;. We will respond within <strong className="text-white">30 days</strong>.
                  </p>
                  <p>
                    If you are in the EU/UK and are unsatisfied with our response, you have the right to lodge a complaint with your supervisory authority (e.g., ICO in the UK, relevant EU DPA).
                  </p>
                </SectionCard>

                {/* 11. Candidate Rights */}
                <SectionCard id="candidate-rights" icon={UserCheck} iconColor="bg-teal-500/20 text-teal-400" title="Specific Rights for Interview Candidates">
                  <p>
                    If you are a candidate who has participated in a GreenTick AI interview — whether through a corporate client or an institution — you have specific rights in addition to the general rights above:
                  </p>
                  <BulletList items={[
                    'Right to know the specific job role and employer for which you were evaluated',
                    'Right to access your evaluation report and scores',
                    'Right to request human review of any AI evaluation that impacted a hiring decision',
                    'Right to know how long your interview recording will be retained',
                    "Right to request deletion of your interview recording (subject to the employer/institution's legal obligations)",
                    'Right to opt out of any further processing by GreenTick if you were added without your prior consent',
                  ]} />
                  <InfoBadge>
                    If you were invited to an interview by a company or university, note that the employer/institution may also be a Data Controller for your data under their own privacy policy.
                  </InfoBadge>
                </SectionCard>

                {/* 12. Cookies */}
                <SectionCard id="cookies" icon={Server} iconColor="bg-orange-500/20 text-orange-400" title="Cookies & Tracking Technologies">
                  <p>
                    We use cookies and similar tracking technologies to enhance your experience, analyse platform usage, and improve our services. Please see our dedicated{' '}
                    <Link to="/cookie-policy" className="text-teal-400 hover:underline">Cookie Policy</Link>{' '}
                    for full details.
                  </p>
                  <p>In summary, we use:</p>
                  <BulletList items={[
                    'Essential cookies (required for the Platform to function — cannot be disabled)',
                    'Performance/analytics cookies (understand usage patterns — can be opted out)',
                    'Functional cookies (remember preferences — can be managed)',
                    'Marketing cookies (only if you have explicitly opted in)',
                  ]} />
                  <p>
                    You can control cookie preferences through your browser settings or our cookie preference center. Disabling certain cookies may impact Platform functionality.
                  </p>
                </SectionCard>

                {/* 13. Children */}
                <SectionCard id="children" icon={AlertCircle} iconColor="bg-rose-500/20 text-rose-400" title="Children's Privacy">
                  <p>
                    GreenTick's Platform is intended for professional and educational use by individuals aged <strong className="text-white">18 and above</strong>. For college/university placements involving students below 18, we require the institution to obtain parental/guardian consent and certify this to us before adding such candidates.
                  </p>
                  <p>
                    If we become aware that we have inadvertently collected personal data from a child under 18 without appropriate consent, we will delete such data promptly. Please contact us at{' '}
                    <a href="mailto:privacy@greentick.org.in" className="text-teal-400 hover:underline">privacy@greentick.org.in</a>{' '}
                    if you believe this has occurred.
                  </p>
                </SectionCard>

                {/* 14. Security */}
                <SectionCard id="security" icon={Lock} iconColor="bg-teal-500/20 text-teal-400" title="Security Measures">
                  <p>
                    We implement and maintain comprehensive technical and organisational measures to protect your data:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { measure: '🔐 AES-256 Encryption', desc: 'Data encrypted at rest and in transit (TLS 1.3).' },
                      { measure: '🔑 Access Controls', desc: 'Role-based access with principle of least privilege.' },
                      { measure: '🛡️ Regular Audits', desc: 'Periodic security audits and penetration testing.' },
                      { measure: '👁️ Monitoring', desc: '24/7 intrusion detection and anomaly monitoring.' },
                      { measure: '🔄 Incident Response', desc: 'Defined breach notification procedures (<72 hours).' },
                      { measure: '🏗️ Secure Infrastructure', desc: 'Hosted on ISO 27001-certified cloud infrastructure.' },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3 bg-slate-800/30 rounded-xl p-4 border border-white/5">
                        <div>
                          <p className="font-semibold text-white text-sm">{item.measure}</p>
                          <p className="text-slate-400 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4">
                    Despite these measures, no system is 100% secure. If you suspect a security incident involving your data, please notify us immediately at{' '}
                    <a href="mailto:security@greentick.org.in" className="text-teal-400 hover:underline">security@greentick.org.in</a>.
                  </p>
                </SectionCard>

                {/* 15. Policy Updates */}
                <SectionCard id="policy-updates" icon={FileText} iconColor="bg-slate-500/20 text-slate-400" title="Changes to This Policy">
                  <p>
                    We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or platform features. When we make material changes, we will:
                  </p>
                  <BulletList items={[
                    'Update the "Last Updated" date at the top of this policy',
                    'Send an email notification to registered users for significant changes',
                    'Display a prominent in-Platform banner for at least 30 days after major updates',
                    'Obtain renewed consent where legally required',
                  ]} />
                  <p>
                    We encourage you to review this policy periodically. Your continued use of the Platform after any changes constitutes acceptance of the updated policy for non-material changes.
                  </p>
                </SectionCard>

                {/* 16. Contact */}
                <SectionCard id="contact" icon={Mail} iconColor="bg-teal-500/20 text-teal-400" title="Contact Us & Data Protection Officer">
                  <p>
                    If you have any questions, concerns, or wish to exercise your data rights, please contact us through any of the following means:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                    <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-5">
                      <p className="text-teal-300 font-semibold mb-1">Privacy & Data Rights</p>
                      <a href="mailto:privacy@greentick.org.in" className="text-white hover:text-teal-300 transition-colors">
                        privacy@greentick.org.in
                      </a>
                      <p className="text-slate-400 text-xs mt-2">Response within 30 days</p>
                    </div>
                    <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-5">
                      <p className="text-rose-300 font-semibold mb-1">Security Incidents</p>
                      <a href="mailto:security@greentick.org.in" className="text-white hover:text-rose-300 transition-colors">
                        security@greentick.org.in
                      </a>
                      <p className="text-slate-400 text-xs mt-2">For suspected breaches</p>
                    </div>
                    <div className="bg-slate-700/40 border border-white/10 rounded-xl p-5">
                      <p className="text-slate-300 font-semibold mb-1">General Enquiries</p>
                      <a href="mailto:hello@greentick.org.in" className="text-white hover:text-teal-300 transition-colors">
                        hello@greentick.org.in
                      </a>
                    </div>
                    <div className="bg-slate-700/40 border border-white/10 rounded-xl p-5">
                      <p className="text-slate-300 font-semibold mb-1">Contact Page</p>
                      <Link to="/contact" className="text-teal-400 hover:underline">
                        greentick.org.in/contact
                      </Link>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-4">
                    <Link
                      to="/terms-of-service"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-500/10 text-teal-300 border border-teal-500/20 hover:bg-teal-500/20 transition-all text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      Terms of Service
                    </Link>
                    <Link
                      to="/cookie-policy"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-500/10 text-teal-300 border border-teal-500/20 hover:bg-teal-500/20 transition-all text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      Cookie Policy
                    </Link>
                  </div>
                </SectionCard>

              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
